import React, { useMemo } from 'react';
import '../../pages/community/PublicConvocationPage.css';

// Componente compartilhado: renderiza a quadra top-down com jogadores
// confirmados (going) posicionados em spots taticos + Espera (waiting) no
// topo. Mini-players SVG. Animacao fadeInPop automatica quando novos
// jogadores aparecem entre re-renders (via React key = player id).
//
// Props:
// - going:  [{ id, name }]  jogadores confirmados
// - waiting: [{ id, name }] jogadores na espera
// - highlightName?: string  destaca jogador com esse nome (aro dourado)
// - emptyLabel?: string     texto pra mostrar quando nao ha ninguem

const FIELD_SPOTS = [
  { left: '50%', top: '58%' },
  { left: '30%', top: '30%' }, { left: '70%', top: '30%' },
  { left: '22%', top: '48%' }, { left: '78%', top: '48%' },
  { left: '50%', top: '38%' },
  { left: '35%', top: '68%' }, { left: '65%', top: '68%' },
  { left: '45%', top: '78%' }, { left: '55%', top: '78%' },
  { left: '40%', top: '22%' }, { left: '60%', top: '22%' },
];
const WAITING_SPOTS = [
  { left: '50%', top: '9%' }, { left: '32%', top: '9%' }, { left: '68%', top: '9%' },
  { left: '20%', top: '9%' }, { left: '80%', top: '9%' },
];
const OUT_SPOTS = [
  { left: '50%', top: '93%' }, { left: '32%', top: '93%' }, { left: '68%', top: '93%' },
  { left: '20%', top: '93%' }, { left: '80%', top: '93%' },
];
const SKIN_TONES = ['#f2c99a', '#e6b48c', '#c69371', '#a56a45', '#8b5a3c', '#623a25'];
function skinFor(name) {
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return SKIN_TONES[h % SKIN_TONES.length];
}
function firstName(name) {
  return String(name || '').split(' ')[0] || '?';
}

function MiniPlayer({ shirt, skin }) {
  return (
    <svg className="pcp-mini-player" viewBox="0 0 40 60" aria-hidden="true">
      <ellipse className="pcp-shadow-el" cx="20" cy="55" rx="10" ry="1.8" fill="rgba(0,0,0,0.55)"/>
      <g className="pcp-leg pcp-leg-left"><rect x="14.5" y="40" width="4" height="14" rx="1.8" fill="#0f172a"/></g>
      <g className="pcp-leg pcp-leg-right"><rect x="21.5" y="40" width="4" height="14" rx="1.8" fill="#0f172a"/></g>
      <g className="pcp-arm pcp-arm-left"><rect x="6.5" y="22" width="3.5" height="14" rx="1.6" fill={skin}/></g>
      <g className="pcp-arm pcp-arm-right"><rect x="30" y="22" width="3.5" height="14" rx="1.6" fill={skin}/></g>
      <path d="M 10 22 L 10 42 Q 10 45 13 45 L 27 45 Q 30 45 30 42 L 30 22 Z" fill={shirt}/>
      <path d="M 16 22 L 20 26 L 24 22 Z" fill="rgba(0,0,0,0.25)"/>
      <g className="pcp-head-grp">
        <circle cx="20" cy="14" r="7" fill={skin}/>
        <circle cx="17.5" cy="13.5" r="0.8" fill="#0f172a"/>
        <circle cx="22.5" cy="13.5" r="0.8" fill="#0f172a"/>
        <path d="M 17.5 16 Q 20 17.5 22.5 16" stroke="#0f172a" strokeWidth="0.8" fill="none" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

export default function StadiumField({ going = [], waiting = [], notGoing = [], highlightName = '', emptyLabel = 'Aguardando confirmações…' }) {
  const positioned = useMemo(() => {
    const items = [];
    going.slice(0, FIELD_SPOTS.length).forEach((p, i) => {
      items.push({
        id: p.id ?? `g-${i}`, name: firstName(p.name), full: p.name, kind: 'going',
        ...FIELD_SPOTS[i], shirt: '#22c55e', skin: skinFor(p.name),
      });
    });
    waiting.slice(0, WAITING_SPOTS.length).forEach((p, i) => {
      items.push({
        id: p.id ?? `w-${i}`, name: firstName(p.name), full: p.name, kind: 'waiting',
        ...WAITING_SPOTS[i], shirt: '#94a3b8', skin: skinFor(p.name),
      });
    });
    notGoing.slice(0, OUT_SPOTS.length).forEach((p, i) => {
      items.push({
        id: p.id ?? `o-${i}`, name: firstName(p.name), full: p.name, kind: 'out',
        ...OUT_SPOTS[i], shirt: '#475569', skin: skinFor(p.name),
      });
    });
    return items;
  }, [going, waiting, notGoing]);

  const hlNorm = String(highlightName || '').trim().toLowerCase();

  return (
    <div className="pcp-stadium-wrap">
      <svg className="pcp-stadium" viewBox="0 0 300 400" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <pattern id="sf-stripes" x="0" y="0" width="300" height="50" patternUnits="userSpaceOnUse">
            <rect width="300" height="50" fill="#0a4a2e"/>
            <rect y="25" width="300" height="25" fill="#0d5c39"/>
          </pattern>
        </defs>
        <rect width="300" height="400" fill="url(#sf-stripes)"/>
        <rect x="14" y="14" width="272" height="372" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2" rx="2"/>
        <line x1="14" y1="200" x2="286" y2="200" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
        <circle cx="150" cy="200" r="42" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
        <circle cx="150" cy="200" r="3" fill="rgba(244,244,245,0.85)"/>
        <rect x="70" y="14" width="160" height="60" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
        <rect x="105" y="14" width="90" height="24" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
        <circle cx="150" cy="60" r="2.5" fill="rgba(244,244,245,0.85)"/>
        <path d="M 120 74 A 34 34 0 0 0 180 74" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
        <rect x="70" y="326" width="160" height="60" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
        <rect x="105" y="362" width="90" height="24" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
        <circle cx="150" cy="340" r="2.5" fill="rgba(244,244,245,0.85)"/>
        <path d="M 120 326 A 34 34 0 0 1 180 326" fill="none" stroke="rgba(244,244,245,0.85)" strokeWidth="2"/>
      </svg>

      <div className="pcp-zone-label pcp-zone-espera">Espera</div>
      {notGoing.length > 0 && <div className="pcp-zone-label pcp-zone-fora">Fora</div>}

      <div className="pcp-confirmed-layer">
        {positioned.map((p) => {
          const isHl = hlNorm && String(p.full || '').toLowerCase().includes(hlNorm);
          return (
            <div key={p.id}
                 className={`pcp-confirmed-player is-${p.kind} ${isHl ? 'is-highlight' : ''}`}
                 style={{ left: p.left, top: p.top }}>
              <MiniPlayer shirt={p.shirt} skin={p.skin} />
              <div className="pcp-player-name">{p.name}</div>
            </div>
          );
        })}
      </div>

      {positioned.length === 0 && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 5,
          color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600,
          textShadow: '0 2px 6px rgba(0,0,0,0.6)',
        }}>
          {emptyLabel}
        </div>
      )}
    </div>
  );
}
